import { RouteRecordRaw } from "vue-router";

export type AppRouteMeta = {
  requiresAuth?: boolean;
  restoreHashOnIdChange?: boolean;
};

// special routes
export const unmatched = "/:pathMatch(.*)*";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Resume Listing",
    component: () => import("@/features/resume/views/ResumesPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/resumes/:id",
    name: "Resume",
    props: (route) => ({ id: Number(route.params.id) }),
    component: () => import("@/features/resume/views/ResumePage.vue"),
    meta: { requiresAuth: true, restoreHashOnIdChange: true } satisfies AppRouteMeta,
  },
  {
    path: "/employee-upload",
    name: "Employee Upload",
    component: () => import("@/features/employee-upload/views/EmployeeUploadPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/metrics",
    name: "Metrics",
    component: () => import("@/features/metrics/views/MetricsPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/employees",
    name: "Employees",
    component: () => import("@/features/employees/views/EmployeesPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/managers",
    name: "Managers",
    component: () => import("@/features/managers/views/ManagersPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/search",
    name: "Advanced Search",
    component: () => import("@/features/advanced-search/views/AdvancedSearchPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/reconcile-data",
    name: "Reconcile Data",
    component: () => import("@/features/reconcile-data/views/ReconcileDataPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/FAQ",
    name: "FAQ",
    component: () => import("@/features/faq/views/FAQPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/contact",
    name: "Contact Us",
    component: () => import("@/features/contact-us/views/ContactPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/resume-tracker",
    name: "Resume Tracker",
    component: () => import("@/features/resume-tracker/views/ResumeTrackerPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/change-review",
    name: "Resume Change Management",
    component: () => import("@/features/change-review/views/ChangeLogPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/release-notes",
    name: "Release Notes",
    component: () => import("@/features/release-notes/views/ReleaseNotesPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/transfer-data",
    name: "Transfer Data",
    component: () => import("@/features/transfer-data/views/TransferDataPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/data-request",
    name: "Data Requests",
    component: () => import("@/features/data-requests/views/DataRequestsPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/resume-exclusion",
    name: "Resume Exclusion",
    component: () => import("@/features/resume-exclusions/views/ResumeExclusionPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/regional-leaders",
    name: "Regional Leaders",
    component: () => import("@/features/regional-leaders/views/RegionalLeadersPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/ai-scheduled",
    name: "Scheduled for AI",
    component: () => import("@/features/ai-review/views/ScheduledForAIScoringPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/permission-management",
    name: "Permission Management",
    component: () => import("@/features/permissions/views/PermissionManagementPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/issues",
    name: "Issues",
    component: () => import("@/features/issues/views/IssuesPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/features/login/views/LoginPage.vue"),
    meta: { requiresAuth: false } satisfies AppRouteMeta,
  },
  {
    path: "/imports",
    name: "Imports",
    component: () => import("@/features/resume-imports/views/ImportsPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/notifications",
    name: "System Notifications",
    component: () => import("@/features/notifications/views/NotificationsPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/preview",
    name: "Preview",
    component: () => import("@/features/resume-preview/views/ResumePreviewPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/resume-flags",
    name: "Resume Flags",
    component: () => import("@/features/resume-flagging/views/ResumeFlagAdministrationPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/employees/:id",
    name: "Employee Profile",
    props: (route) => ({ id: Number(route.params.id) }),
    component: () => import("@/features/employees/views/EmployeeProfilePage.vue"),
    meta: { requiresAuth: true, restoreHashOnIdChange: true } satisfies AppRouteMeta,
  },
  {
    path: "/api-keys",
    name: "API Key Admin",
    component: () => import("@/features/api-keys/views/ApiKeyAdministrationPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/skill-administration",
    name: "Skill Administration",
    component: () => import("@/features/skill-administration/views/SkillAdministrationPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/configuration",
    name: "Configuration",
    component: () => import("@/features/configuration/views/ConfigurationDashboardPage.vue"),
    meta: { requiresAuth: true } satisfies AppRouteMeta,
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
    meta: { requiresAuth: false } satisfies AppRouteMeta,
  },
];
